// System Settings Manager Component
// Allows updating environment variables and restarting services
import React, { useState } from 'react';
import { 
  Settings, 
  RefreshCw, 
  Save, 
  Eye, 
  EyeOff, 
  AlertTriangle,
  CheckCircle,
  Server,
  Mail,
  Key,
  Globe
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SystemManager = () => {
  const [loading, setLoading] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [showConfirmRestart, setShowConfirmRestart] = useState(false);
  
  // Environment variables state (values are never shown, only updated)
  const [envVars, setEnvVars] = useState({
    // Google OAuth
    GOOGLE_CLIENT_ID: '',
    GOOGLE_CLIENT_SECRET: '',
    // Email (Google Workspace SMTP or Resend)
    EMAIL_PROVIDER: 'google', // 'google' or 'resend'
    SMTP_HOST: '',
    SMTP_PORT: '',
    SMTP_USER: '',
    SMTP_PASSWORD: '',
    RESEND_API_KEY: '',
    SENDER_EMAIL: '',
    RECIPIENT_EMAIL: '',
    // Analytics
    GA_MEASUREMENT_ID: '',
    // Tawk.to
    TAWK_PROPERTY_ID: '',
    TAWK_WIDGET_ID_EN: '',
    TAWK_WIDGET_ID_FR: '',
    TAWK_WIDGET_ID_AR: '',
  });

  const [showPassword, setShowPassword] = useState({});

  const handleChange = (key, value) => {
    setEnvVars(prev => ({ ...prev, [key]: value }));
  };

  const toggleShowPassword = (key) => {
    setShowPassword(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSaveEnvVars = async (section) => {
    setLoading(true);
    try {
      // Filter only non-empty values for the section
      let varsToUpdate = {};
      
      if (section === 'oauth') {
        if (envVars.GOOGLE_CLIENT_ID) varsToUpdate.GOOGLE_CLIENT_ID = envVars.GOOGLE_CLIENT_ID;
        if (envVars.GOOGLE_CLIENT_SECRET) varsToUpdate.GOOGLE_CLIENT_SECRET = envVars.GOOGLE_CLIENT_SECRET;
      } else if (section === 'email') {
        varsToUpdate.EMAIL_PROVIDER = envVars.EMAIL_PROVIDER;
        if (envVars.EMAIL_PROVIDER === 'google') {
          if (envVars.SMTP_HOST) varsToUpdate.SMTP_HOST = envVars.SMTP_HOST;
          if (envVars.SMTP_PORT) varsToUpdate.SMTP_PORT = envVars.SMTP_PORT;
          if (envVars.SMTP_USER) varsToUpdate.SMTP_USER = envVars.SMTP_USER;
          if (envVars.SMTP_PASSWORD) varsToUpdate.SMTP_PASSWORD = envVars.SMTP_PASSWORD;
        } else {
          if (envVars.RESEND_API_KEY) varsToUpdate.RESEND_API_KEY = envVars.RESEND_API_KEY;
        }
        if (envVars.SENDER_EMAIL) varsToUpdate.SENDER_EMAIL = envVars.SENDER_EMAIL;
        if (envVars.RECIPIENT_EMAIL) varsToUpdate.RECIPIENT_EMAIL = envVars.RECIPIENT_EMAIL;
      } else if (section === 'analytics') {
        if (envVars.GA_MEASUREMENT_ID) varsToUpdate.GA_MEASUREMENT_ID = envVars.GA_MEASUREMENT_ID;
      } else if (section === 'tawk') {
        if (envVars.TAWK_PROPERTY_ID) varsToUpdate.TAWK_PROPERTY_ID = envVars.TAWK_PROPERTY_ID;
        if (envVars.TAWK_WIDGET_ID_EN) varsToUpdate.TAWK_WIDGET_ID_EN = envVars.TAWK_WIDGET_ID_EN;
        if (envVars.TAWK_WIDGET_ID_FR) varsToUpdate.TAWK_WIDGET_ID_FR = envVars.TAWK_WIDGET_ID_FR;
        if (envVars.TAWK_WIDGET_ID_AR) varsToUpdate.TAWK_WIDGET_ID_AR = envVars.TAWK_WIDGET_ID_AR;
      }

      const response = await fetch(`${BACKEND_URL}/api/admin/system/env`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ variables: varsToUpdate, section })
      });

      if (response.ok) {
        toast.success('Configuration mise à jour! Redémarrez l\'application pour appliquer.');
        // Clear the fields after save
        if (section === 'oauth') {
          setEnvVars(prev => ({ ...prev, GOOGLE_CLIENT_ID: '', GOOGLE_CLIENT_SECRET: '' }));
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleRestart = async () => {
    setRestarting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/system/restart`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Redémarrage en cours... La page va se recharger dans 10 secondes.');
        setTimeout(() => {
          window.location.reload();
        }, 10000);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors du redémarrage');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setRestarting(false);
      setShowConfirmRestart(false);
    }
  };

  const PasswordInput = ({ label, keyName, placeholder }) => (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
      <div className="relative">
        <input
          type={showPassword[keyName] ? 'text' : 'password'}
          value={envVars[keyName]}
          onChange={(e) => handleChange(keyName, e.target.value)}
          placeholder={placeholder}
          className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <button
          type="button"
          onClick={() => toggleShowPassword(keyName)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
        >
          {showPassword[keyName] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Configuration Système
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Gérez les variables d'environnement et redémarrez l'application
          </p>
        </div>
      </div>

      {/* Warning */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-800">
          <strong>Attention:</strong> Les modifications de configuration nécessitent un redémarrage de l'application pour prendre effet.
          Les valeurs actuelles ne sont jamais affichées pour des raisons de sécurité.
        </div>
      </div>

      {/* Google OAuth Section */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <h3 className="font-medium text-slate-800 mb-4 flex items-center gap-2">
          <Key className="w-4 h-4" />
          Google OAuth (Authentification Admin)
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <PasswordInput 
            label="Client ID" 
            keyName="GOOGLE_CLIENT_ID" 
            placeholder="Entrez le nouveau Client ID..."
          />
          <PasswordInput 
            label="Client Secret" 
            keyName="GOOGLE_CLIENT_SECRET" 
            placeholder="Entrez le nouveau Client Secret..."
          />
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={() => handleSaveEnvVars('oauth')} disabled={loading}>
            <Save className="w-4 h-4 mr-2" />
            Enregistrer OAuth
          </Button>
        </div>
      </div>

      {/* Email Configuration Section */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <h3 className="font-medium text-slate-800 mb-4 flex items-center gap-2">
          <Mail className="w-4 h-4" />
          Configuration Email
        </h3>
        
        {/* Provider Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-2">Fournisseur Email</label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="email_provider"
                value="google"
                checked={envVars.EMAIL_PROVIDER === 'google'}
                onChange={(e) => handleChange('EMAIL_PROVIDER', e.target.value)}
                className="w-4 h-4 text-blue-600"
              />
              <span className="text-sm">Google Workspace SMTP</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="email_provider"
                value="resend"
                checked={envVars.EMAIL_PROVIDER === 'resend'}
                onChange={(e) => handleChange('EMAIL_PROVIDER', e.target.value)}
                className="w-4 h-4 text-blue-600"
              />
              <span className="text-sm">Resend API</span>
            </label>
          </div>
        </div>

        {/* Google SMTP Fields */}
        {envVars.EMAIL_PROVIDER === 'google' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 p-4 bg-slate-50 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Serveur SMTP</label>
              <input
                type="text"
                value={envVars.SMTP_HOST}
                onChange={(e) => handleChange('SMTP_HOST', e.target.value)}
                placeholder="smtp.gmail.com"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Port SMTP</label>
              <input
                type="text"
                value={envVars.SMTP_PORT}
                onChange={(e) => handleChange('SMTP_PORT', e.target.value)}
                placeholder="587"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email SMTP</label>
              <input
                type="email"
                value={envVars.SMTP_USER}
                onChange={(e) => handleChange('SMTP_USER', e.target.value)}
                placeholder="noreply@sae.dz"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              />
            </div>
            <PasswordInput 
              label="Mot de passe d'application" 
              keyName="SMTP_PASSWORD" 
              placeholder="Mot de passe d'application Google..."
            />
          </div>
        )}

        {/* Resend Fields */}
        {envVars.EMAIL_PROVIDER === 'resend' && (
          <div className="mb-4 p-4 bg-slate-50 rounded-lg">
            <PasswordInput 
              label="Clé API Resend" 
              keyName="RESEND_API_KEY" 
              placeholder="re_..."
            />
          </div>
        )}

        {/* Common Email Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email expéditeur</label>
            <input
              type="email"
              value={envVars.SENDER_EMAIL}
              onChange={(e) => handleChange('SENDER_EMAIL', e.target.value)}
              placeholder="noreply@sae.dz"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email destinataire (notifications)</label>
            <input
              type="email"
              value={envVars.RECIPIENT_EMAIL}
              onChange={(e) => handleChange('RECIPIENT_EMAIL', e.target.value)}
              placeholder="contact@sae.dz"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg"
            />
          </div>
        </div>
        
        <div className="mt-4 flex justify-end">
          <Button onClick={() => handleSaveEnvVars('email')} disabled={loading}>
            <Save className="w-4 h-4 mr-2" />
            Enregistrer Email
          </Button>
        </div>
      </div>

      {/* Google Analytics Section */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <h3 className="font-medium text-slate-800 mb-4 flex items-center gap-2">
          <Globe className="w-4 h-4" />
          Google Analytics
        </h3>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Measurement ID</label>
          <input
            type="text"
            value={envVars.GA_MEASUREMENT_ID}
            onChange={(e) => handleChange('GA_MEASUREMENT_ID', e.target.value)}
            placeholder="G-XXXXXXXXXX"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg"
          />
          <p className="text-xs text-slate-500 mt-1">Obtenez votre ID sur analytics.google.com</p>
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={() => handleSaveEnvVars('analytics')} disabled={loading}>
            <Save className="w-4 h-4 mr-2" />
            Enregistrer Analytics
          </Button>
        </div>
      </div>

      {/* Restart Section */}
      <div className="bg-white border border-red-200 rounded-lg p-6">
        <h3 className="font-medium text-slate-800 mb-4 flex items-center gap-2">
          <Server className="w-4 h-4" />
          Redémarrage de l'Application
        </h3>
        <p className="text-sm text-slate-600 mb-4">
          Redémarrez le frontend et le backend pour appliquer les nouvelles configurations.
          L'application sera indisponible pendant quelques secondes.
        </p>
        
        {!showConfirmRestart ? (
          <Button 
            variant="destructive" 
            onClick={() => setShowConfirmRestart(true)}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Redémarrer l'Application
          </Button>
        ) : (
          <div className="flex items-center gap-4">
            <span className="text-sm text-red-600 font-medium">Êtes-vous sûr ?</span>
            <Button 
              variant="destructive" 
              onClick={handleRestart}
              disabled={restarting}
            >
              {restarting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Redémarrage...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Confirmer
                </>
              )}
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setShowConfirmRestart(false)}
              disabled={restarting}
            >
              Annuler
            </Button>
          </div>
        )}
      </div>

      {/* Help Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
        <strong>💡 Configuration Google Workspace SMTP:</strong>
        <ol className="mt-2 space-y-1 list-decimal list-inside">
          <li>Activez l'accès aux applications moins sécurisées OU créez un mot de passe d'application</li>
          <li>Serveur: smtp.gmail.com | Port: 587 (TLS) ou 465 (SSL)</li>
          <li>Utilisez votre email @sae.dz comme nom d'utilisateur</li>
          <li>Le mot de passe d'application se génère dans les paramètres de sécurité Google</li>
        </ol>
      </div>
    </div>
  );
};

export default SystemManager;
